import { useSyncExternalStore } from 'react';

/**
 * Start opens the live poll for people already on its page (TECH-867 § Decided "Start opens it for people already
 * there"; CONTRACT § 7.1): the SSE handler (`useRealtimeInvalidation`) hands `live_poll.started` to whichever page is
 * on screen through this channel, and the page (`useLivePollTakeover`) decides whether it is its own poll.
 */
export interface LivePollStarted {
  questionId: string;
  spaceId: string | null;
  eventId: string | null;
  /** The author's full name; the line shows the first name ("João started a live poll"). */
  authorName: string;
}

type Listener = (started: LivePollStarted) => void;
const listeners = new Set<Listener>();

export const announceLivePollStarted = (started: LivePollStarted): void => {
  listeners.forEach(listener => {
    listener(started);
  });
};

export const subscribeLivePollStarted = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// Once per person per poll: the polls a person's page already opened, in localStorage (a reconnect's replay, a
// reload or another tab never opens it again). Kept short: only the newest few matter.
const SEEN_KEY_PREFIX = 'meetball.live.takeoverSeen.';
const SEEN_KEEP = 50;

/**
 * Only when storage refuses a write (private mode, blocked site data, a full quota): remembered for this page's life.
 * Read before storage: a storage whose `setItem` threw may still answer `getItem` with the older list.
 */
const seenInMemory = new Map<string, string[]>();

const readSeen = (userId: string): string[] => {
  const kept = seenInMemory.get(userId);
  if (kept !== undefined) return kept;
  try {
    const raw = localStorage.getItem(`${SEEN_KEY_PREFIX}${userId}`);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

export const hasSeenTakeover = (userId: string, questionId: string): boolean =>
  readSeen(userId).includes(questionId);

export const markTakeoverSeen = (userId: string, questionId: string): void => {
  const next = [...readSeen(userId).filter(id => id !== questionId), questionId].slice(-SEEN_KEEP);
  try {
    localStorage.setItem(`${SEEN_KEY_PREFIX}${userId}`, JSON.stringify(next));
    seenInMemory.delete(userId);
  } catch {
    seenInMemory.set(userId, next);
  }
};

/**
 * The polls Start opened on a person's pages and not yet ended: their LIVE bar stays pinned to that page's feed
 * until End (L14b), across a remount (a post opened and Back, another Space tab) and a reload. "Seen" alone only
 * stops a second takeover; this keeps the bar. Dropped at End, a delete, or when the poll turns out to be theirs.
 */
export interface ActiveTakeover {
  questionId: string;
  authorName: string;
  spaceId: string | null;
  eventId: string | null;
}

const ACTIVE_KEY_PREFIX = 'meetball.live.takeoverActive.';
const ACTIVE_KEEP = 10;
const activeInMemory = new Map<string, ActiveTakeover[]>();
let activeVersion = 0;
const activeListeners = new Set<() => void>();

const isActiveTakeover = (value: unknown): value is ActiveTakeover => {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.questionId === 'string' &&
    typeof entry.authorName === 'string' &&
    (entry.spaceId === null || typeof entry.spaceId === 'string') &&
    (entry.eventId === null || typeof entry.eventId === 'string')
  );
};

// Memory over storage, as `readSeen`: a write storage refused (setItem threw, getItem still works) must not lose
// the new entry, or the Start takeover never opens (MERGE-FE review).
export const readActiveTakeovers = (userId: string): ActiveTakeover[] => {
  const kept = activeInMemory.get(userId);
  if (kept !== undefined) return kept;
  try {
    const raw = localStorage.getItem(`${ACTIVE_KEY_PREFIX}${userId}`);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isActiveTakeover) : [];
  } catch {
    return [];
  }
};

const notifyActive = (): void => {
  activeVersion += 1;
  activeListeners.forEach(listener => {
    listener();
  });
};

const writeActiveTakeovers = (userId: string, next: ActiveTakeover[]): void => {
  try {
    localStorage.setItem(`${ACTIVE_KEY_PREFIX}${userId}`, JSON.stringify(next));
    activeInMemory.delete(userId);
  } catch {
    activeInMemory.set(userId, next);
  }
  notifyActive();
};

export const rememberActiveTakeover = (userId: string, entry: ActiveTakeover): void => {
  const others = readActiveTakeovers(userId).filter(item => item.questionId !== entry.questionId);
  writeActiveTakeovers(userId, [...others, entry].slice(-ACTIVE_KEEP));
};

export const forgetActiveTakeover = (userId: string, questionId: string): void => {
  const all = readActiveTakeovers(userId);
  const next = all.filter(item => item.questionId !== questionId);
  if (next.length !== all.length) writeActiveTakeovers(userId, next);
};

/**
 * Every stored takeover record's prefix: they name who started a poll and in which (maybe private) Space, so the
 * sign-out and account-switch sweeps (`storageCleanup.ts`) remove them.
 */
export const TAKEOVER_STORAGE_PREFIXES = [SEEN_KEY_PREFIX, ACTIVE_KEY_PREFIX] as const;

/**
 * Forgets every person's takeover records, stored and in memory (no storage walk reaches memory), THEN tells the
 * LIVE bars: a bar that re-read before the stored keys were gone would cache the old list with no later bump.
 */
export const forgetAllTakeovers = (): void => {
  seenInMemory.clear();
  activeInMemory.clear();
  try {
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key && TAKEOVER_STORAGE_PREFIXES.some(prefix => key.startsWith(prefix))) keys.push(key);
    }
    keys.forEach(key => {
      localStorage.removeItem(key);
    });
  } catch {
    // Storage blocked: nothing was kept there.
  }
  notifyActive();
};

const subscribeActive = (listener: () => void) => {
  activeListeners.add(listener);
  return () => {
    activeListeners.delete(listener);
  };
};

const NO_ACTIVE: ActiveTakeover[] = [];
let activeSnapshot: { version: number; userId: string | null; list: ActiveTakeover[] } = {
  version: -1,
  userId: null,
  list: NO_ACTIVE,
};

/** The person's open takeovers, re-read after every write (value-stable between writes, for the store). */
export const useActiveTakeovers = (userId: string | null): ActiveTakeover[] =>
  useSyncExternalStore(
    subscribeActive,
    () => {
      if (!userId) return NO_ACTIVE;
      if (activeSnapshot.version !== activeVersion || activeSnapshot.userId !== userId)
        activeSnapshot = { version: activeVersion, userId, list: readActiveTakeovers(userId) };
      return activeSnapshot.list;
    },
    () => NO_ACTIVE,
  );

/**
 * The LIVE bar left by the waiting sheet's "While you wait" link (D1): the live link's code and the join Space it
 * opened. The Space page with that slug shows the bar (it reads `/state`) and opens the sheet again by itself when
 * question 1 is shown. One at a time, in memory (a reload of the Space page drops it).
 */
export interface LiveWaitBar {
  code: string;
  spaceSlug: string;
}

let waitBar: LiveWaitBar | null = null;
const waitBarListeners = new Set<() => void>();

export const setLiveWaitBar = (next: LiveWaitBar | null): void => {
  waitBar = next;
  waitBarListeners.forEach(listener => {
    listener();
  });
};

const subscribeWaitBar = (listener: () => void) => {
  waitBarListeners.add(listener);
  return () => {
    waitBarListeners.delete(listener);
  };
};

export const useLiveWaitBar = (): LiveWaitBar | null =>
  useSyncExternalStore(
    subscribeWaitBar,
    () => waitBar,
    () => null,
  );
